import { useEffect, useState } from 'react'

import { isReplyWindowOpen } from './replyWindow'

/** `setTimeout` fires at once for delays past this (about 24.8 days). */
const MAX_TIMEOUT_MS = 2_147_483_647

/**
 * Whether we can still reply, kept current while the thread stays open: a
 * timer re-checks at the expiry so the form gives way to the closed-window
 * notice without any user activity. A pinned `now` (tests, stories) holds
 * the answer still.
 */
export function useReplyWindowOpen(
  expiresAt: string | null,
  now?: Date,
): boolean {
  const [checkedAt, setCheckedAt] = useState(() => Date.now())
  const open = isReplyWindowOpen(expiresAt, now)

  useEffect(() => {
    if (!open || !expiresAt || now) return undefined
    const delay = Math.min(
      new Date(expiresAt).getTime() - Date.now(),
      MAX_TIMEOUT_MS,
    )
    const timeout = setTimeout(() => setCheckedAt(Date.now()), delay)
    return (): void => clearTimeout(timeout)
  }, [open, expiresAt, now, checkedAt])

  return open
}
